<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
  inbox: {
    type: Object,
    required: true,
  },
  connectionStatus: {
    type: String,
    default: '',
  },
});

const { tm } = useI18n();

const reauthorizationRequired = computed(() => {
  return props.inbox.reauthorization_required;
});

const WHATSAPP_PROVIDER_LABEL_KEYS = {
  default: 'DEFAULT',
  whatsapp_cloud: 'WHATSAPP_CLOUD',
  baileys: 'BAILEYS',
  evolution: 'EVOLUTION',
  waha: 'WAHA',
  uazapi: 'UAZAPI',
  notificame: 'NOTIFICAME',
};

const CONNECTED_STATUSES = new Set([
  'connected',
  'open',
  'working',
  'online',
  'ready',
]);
const CONNECTING_STATUSES = new Set([
  'connecting',
  'starting',
  'pending',
  'scan_qr_code',
  'scan-qr-code',
  'scan qr code',
  'initializing',
]);

const STATUS_LABEL_KEYS = {
  connected: 'CONNECTED',
  connecting: 'CONNECTING',
  disconnected: 'DISCONNECTED',
  neutral: 'UNKNOWN',
};

const providerMessages = computed(() => {
  const messages = tm?.('INBOX_MGMT.WHATSAPP_PROVIDER_LABELS');
  return messages && typeof messages === 'object' ? messages : {};
});

const connectionStatusMessages = computed(() => {
  const messages = tm?.('INBOX_MGMT.WHATSAPP_CONNECTION_STATUS');
  return messages && typeof messages === 'object' ? messages : {};
});

const isWhatsappLike = computed(() => {
  const inbox = props.inbox || {};
  if (inbox.channel_type === 'Channel::Whatsapp') {
    return true;
  }

  if (
    inbox.channel_type === 'Channel::TwilioSms' &&
    inbox.medium === 'whatsapp'
  ) {
    return true;
  }

  return false;
});

const normalizedStatus = computed(() =>
  (props.connectionStatus || props.inbox.provider_connection_status || '')
    .toString()
    .trim()
    .toLowerCase()
);

const statusCategory = computed(() => {
  if (!isWhatsappLike.value) {
    return 'neutral';
  }

  const status = normalizedStatus.value;
  if (!status) {
    return 'neutral';
  }

  if (CONNECTED_STATUSES.has(status)) {
    return 'connected';
  }

  if (CONNECTING_STATUSES.has(status)) {
    return 'connecting';
  }

  return 'disconnected';
});

const statusContainerClasses = computed(() => {
  const classes = [
    'size-5',
    'grid',
    'place-content-center',
    'rounded-full',
    'transition-colors',
  ];

  if (props.active) {
    classes.push('ring-2', 'ring-n-solid-blue/60');
  }

  switch (statusCategory.value) {
    case 'connected':
      classes.push('border', 'border-green-400', 'bg-green-100');
      break;
    case 'connecting':
      classes.push('border', 'border-yellow-400', 'bg-yellow-100');
      break;
    case 'disconnected':
      classes.push('border', 'border-red-400', 'bg-red-100');
      break;
    default:
      classes.push('border', 'border-n-strong', 'bg-n-alpha-2');
      break;
  }

  return classes.join(' ');
});

const statusIconClasses = computed(() => {
  const classes = [];

  switch (statusCategory.value) {
    case 'connected':
      classes.push('text-green-700');
      break;
    case 'connecting':
      classes.push('text-yellow-700');
      break;
    case 'disconnected':
      classes.push('text-red-700');
      break;
    default:
      classes.push('text-n-slate-10');
      break;
  }

  if (props.active) {
    classes.push('drop-shadow-sm');
  }

  return classes.join(' ');
});

const providerLabel = computed(() => {
  if (!isWhatsappLike.value) {
    return '';
  }

  const providerKey = WHATSAPP_PROVIDER_LABEL_KEYS[props.inbox.provider];
  if (!providerKey) {
    return '';
  }

  const label = providerMessages.value?.[providerKey];
  return typeof label === 'string' ? label : '';
});

const statusTooltip = computed(() => {
  if (!isWhatsappLike.value) {
    return '';
  }

  const statusKey =
    STATUS_LABEL_KEYS[statusCategory.value] || STATUS_LABEL_KEYS.neutral;
  const messages = connectionStatusMessages.value;
  const statusLabel = messages?.[statusKey];
  const rawStatus = normalizedStatus.value;
  const fallback =
    typeof statusLabel === 'string'
      ? statusLabel
      : rawStatus || messages?.[STATUS_LABEL_KEYS.neutral] || '';

  if (providerLabel.value) {
    return `${providerLabel.value} • ${fallback}`;
  }

  return fallback;
});

const rowClasses = computed(() => {
  const classes = ['flex', 'items-center', 'gap-2', 'w-full'];
  if (props.active) {
    classes.push('text-n-slate-12');
  }
  return classes.join(' ');
});

const labelClasses = computed(() => {
  const classes = ['flex-1', 'truncate', 'min-w-0'];
  if (props.active) {
    classes.push('font-medium');
  }
  return classes.join(' ');
});
</script>

<template>
  <div :class="rowClasses" :data-active="active ? 'true' : null">
    <span
      v-tooltip.top="statusTooltip || null"
      :class="statusContainerClasses"
      :data-status="normalizedStatus"
    >
      <ChannelIcon :inbox="inbox" class="size-3" :class="statusIconClasses" />
    </span>
    <div :class="labelClasses">
      {{ label }}
    </div>
    <div
      v-if="reauthorizationRequired"
      v-tooltip.top-end="$t('SIDEBAR.REAUTHORIZE')"
      class="grid place-content-center size-5 bg-n-ruby-5/60 rounded-full"
    >
      <Icon icon="i-woot-alert" class="size-3 text-n-ruby-9" />
    </div>
  </div>
</template>
